"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, ShieldAlert, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const trustBullets = [{
  id: "bullet-1",
  title: "HPLC purity verified ≥98%",
  detail: "Every batch includes complete mass spectrometry documentation."
}, {
  id: "bullet-2",
  title: "Lyophilized cold-chain packaging",
  detail: "Insulated temperature-controlled transit preserves peptide integrity."
}, {
  id: "bullet-3",
  title: "Fast domestic delivery in India",
  detail: "Direct courier dispatch to university labs and certified research facilities."
}];
const vialImages = [{
  id: "vial-1",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_primary.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_primary.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_primary.png-wvc-sizes",
  alt: "Lyophilized research peptide vial with cobalt aluminum cap",
  label: "RP-01 | purity: 99.4%"
}, {
  id: "vial-2",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_secondary.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_secondary.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_secondary.png-wvc-sizes",
  alt: "Borosilicate glass laboratory vial with teal crimp seal",
  label: "RP-02 | purity: 98.9%"
}, {
  id: "vial-3",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_tertiary.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_tertiary.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/peptide_vial_tertiary.png-wvc-sizes",
  alt: "Research peptide powder vial with crimson safety seal",
  label: "RP-03 | purity: 99.1%"
}];
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const transitionConfig = {
    duration: shouldReduceMotion ? 0.01 : 0.35,
    ease: [0.16, 1, 0.3, 1] as const
  };
  return <section id="hero" data-nav="dark" className="dark bg-background text-foreground relative overflow-hidden border-b border-border/40 py-16 md:py-24">
      {/* Structural analytical background accents */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_75%_30%,hsl(207_85%_33%/0.14),transparent_65%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,hsl(215_60%_14%/0.4)_1px,transparent_1px),linear-gradient(to_bottom,hsl(215_60%_14%/0.4)_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-30" />

      <div className="relative mx-auto max-w-[1088px] px-6 sm:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-10">
          
          {/* Left Column: Technical Context & Action */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={transitionConfig} className="lg:col-span-7 flex flex-col items-start">
            {/* Compliance Kicker */}
            <div className="mb-4 inline-flex items-center gap-2 rounded-sm border border-border/60 bg-muted/20 px-3 py-1 text-xs font-mono tracking-wider text-muted-foreground uppercase">
              <ShieldAlert className="size-3.5 text-primary" />
              <span>RESEARCH USE ONLY — VERIFIED REAGENTS</span>
            </div>

            {/* Main Headline */}
            <h1 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl leading-[1.15]">
              Laboratory-grade peptide synthesis for Indian research institutions
            </h1>

            {/* Explanatory Body */}
            <p className="mt-5 text-base sm:text-lg text-foreground/80 leading-relaxed font-sans max-w-xl">
              Source verified reagents with HPLC validation, detailed batch certificates of analysis, and temperature-controlled cold-chain distribution across India.
            </p>

            {/* Bullet List */}
            <ul className="mt-8 space-y-3.5 border-y border-border/40 py-5 w-full max-w-xl">
              {trustBullets.map((bullet, i) => <li key={bullet.id} data-index={i} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                  <div className="text-sm">
                    <span className="font-medium text-foreground mr-1.5">
                      {bullet.title}
                    </span>
                    <span className="text-foreground/70 font-sans">
                      — {bullet.detail}
                    </span>
                  </div>
                </li>)}
            </ul>

            {/* Primary Action Zone */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-md px-7 font-medium shadow-md">
                <Link to="/ShopPage">
                  Shop Peptides
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-md font-medium">
                <Link to="/ResearchInformationPage">
                  View analytical standards
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Specimen Packaging Cluster */}
          <div className="lg:col-span-5 relative flex justify-center">
            <div className="relative w-full max-w-md">
              {/* Backplate halo */}
              <div className="pointer-events-none absolute -inset-4 rounded-2xl bg-[radial-gradient(circle_at_50%_50%,hsl(207_85%_33%/0.18),transparent_70%)]" />

              <div className="grid grid-cols-2 gap-3 relative">
                {/* Large primary vial presentation */}
                <motion.div initial={shouldReduceMotion ? false : {
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                ...transitionConfig,
                delay: shouldReduceMotion ? 0 : 0.08
              }} data-index={0} className="col-span-2 rounded-lg border border-border/60 bg-card p-4 shadow-md backdrop-blur-xs">
                  <div className="relative flex aspect-16/10 items-center justify-center overflow-hidden rounded-md bg-muted/40 p-3">
                    <img src={vialImages[0].src} data-wvc-srcset={vialImages[0].srcset} data-wvc-sizes={vialImages[0].sizes} alt={vialImages[0].alt} loading="lazy" decoding="async" className="h-full w-auto object-contain" />
                    <div className="absolute top-2 left-2">
                      <Badge variant="outline" className="border-border bg-background/80 font-mono text-[10px] text-foreground">
                        LOT 2408-A
                      </Badge>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between border-t border-border/40 pt-2 text-xs">
                    <span className="font-mono text-muted-foreground">{vialImages[0].label}</span>
                    <span className="font-mono text-foreground/80 font-medium">HPLC + MS PASS</span>
                  </div>
                </motion.div>

                {/* Secondary vial presentation */}
                <motion.div initial={shouldReduceMotion ? false : {
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                ...transitionConfig,
                delay: shouldReduceMotion ? 0 : 0.16
              }} data-index={1} className="rounded-lg border border-border/60 bg-card p-3 shadow-md">
                  <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-md bg-muted/40 p-2">
                    <img src={vialImages[1].src} data-wvc-srcset={vialImages[1].srcset} data-wvc-sizes={vialImages[1].sizes} alt={vialImages[1].alt} loading="lazy" decoding="async" className="h-full w-auto object-contain" />
                  </div>
                  <div className="mt-2 text-[11px] font-mono text-muted-foreground truncate">
                    {vialImages[1].label}
                  </div>
                </motion.div>

                {/* Tertiary vial presentation */}
                <motion.div initial={shouldReduceMotion ? false : {
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                ...transitionConfig,
                delay: shouldReduceMotion ? 0 : 0.24
              }} data-index={2} className="rounded-lg border border-border/60 bg-card p-3 shadow-md">
                  <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-md bg-muted/40 p-2">
                    <img src={vialImages[2].src} data-wvc-srcset={vialImages[2].srcset} data-wvc-sizes={vialImages[2].sizes} alt={vialImages[2].alt} loading="lazy" decoding="async" className="h-full w-auto object-contain" />
                  </div>
                  <div className="mt-2 text-[11px] font-mono text-muted-foreground truncate">
                    {vialImages[2].label}
                  </div>
                </motion.div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}